import {
  type InboxWorkflowCellComponent,
  type InboxWorkflowTableStepConfig,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import AssignRequesterCustomerPreconfiguration from "./_modules/assign-requester-customer-preconfiguration"
import RequesterCustomerDecisionCell from "./_modules/requester-customer-decision-cell"
import RequesterCustomerExistingCell from "./_modules/requester-customer-existing-cell"
import RequesterCustomerPropertyCell from "./_modules/requester-customer-property-cell"
import RequesterCustomerSourceCell from "./_modules/requester-customer-source-cell"
import type {
  AssignRequesterCustomerCommitPayload,
  AssignRequesterCustomerDraft,
  AssignRequesterCustomerPreviewPayload,
  AssignRequesterCustomerRow,
} from "./contract"
import {
  defaultAssignRequesterCustomerDraft,
  validateAssignRequesterCustomerDraft,
} from "./_logic/drafts"
import {
  requesterCustomerDecisionOptions,
  requesterCustomerDecisionPatch,
  requesterCustomerDecisionValue,
  requesterCustomerRoleOptions,
} from "./_logic/options"
import { assignRequesterCustomerPayload } from "./_logic/payload"
import { parseAssignRequesterCustomerPreview } from "./_logic/preview"
import { buildAssignRequesterCustomerRows } from "./_logic/tableRows"

export const assignRequesterCustomerCells: Record<string, InboxWorkflowCellComponent> = {
  requesterCustomerDecision: RequesterCustomerDecisionCell,
  requesterCustomerExisting: RequesterCustomerExistingCell,
  requesterCustomerProperty: RequesterCustomerPropertyCell,
  requesterCustomerSource: RequesterCustomerSourceCell,
}

export const createAssignRequesterCustomerStep = () =>
  ({
    kind: "table",
    preview: {
      kind: "unclear_appointment_feedback.assign_requester_customer.standard_table",
      parse: parseAssignRequesterCustomerPreview,
      errorMessage: "Ansprechpartner-Optionen konnten nicht geladen werden.",
    },
    columns: [
      {
        id: "decision",
        label: "Als was speichern?",
        cellKey: "requesterCustomerDecision",
        placeholder: "Übernahme",
        value: requesterCustomerDecisionValue,
        patch: requesterCustomerDecisionPatch,
        options: requesterCustomerDecisionOptions,
      },
      { id: "addressLabel", label: "Liegenschaft", cellKey: "requesterCustomerProperty" },
      {
        id: "existingCustomers",
        label: "Bisherige Ansprechpartner",
        cellKey: "requesterCustomerExisting",
      },
      {
        id: "displayName",
        label: "Name des Ansprechpartners",
        cellKey: "input",
        inputType: "text",
        placeholder: "Name des Ansprechpartners",
        value: ({ draft }) => draft.displayName,
        patch: (value) => ({ displayName: String(value ?? "").trim() }),
        disabled: ({ draft }) => draft.decision !== "create_new",
      },
      {
        id: "email",
        label: "E-Mail des Ansprechpartners",
        cellKey: "input",
        inputType: "email",
        placeholder: "E-Mail des Ansprechpartners",
        value: ({ draft }) => draft.email,
        patch: (value) => ({ email: String(value ?? "").trim() }),
      },
      {
        id: "phone",
        label: "Telefon des Ansprechpartners",
        cellKey: "input",
        inputType: "tel",
        placeholder: "Telefon des Ansprechpartners",
        value: ({ draft }) => draft.phone,
        patch: (value) => ({ phone: String(value ?? "").trim() }),
      },
      {
        id: "role",
        label: "Rolle des Ansprechpartners",
        cellKey: "select",
        placeholder: "Rolle",
        value: ({ draft }) => draft.role,
        patch: (value) => ({ role: String(value ?? "") as AssignRequesterCustomerDraft["role"] }),
        options: requesterCustomerRoleOptions,
      },
      { id: "sourceLabel", label: "Herkunft", cellKey: "requesterCustomerSource" },
    ],
    buildRows: ({ workflow, step, rowOutcomes, stepPreview }) =>
      buildAssignRequesterCustomerRows({ workflow, step, rowOutcomes, preview: stepPreview }),
    draft: {
      create: defaultAssignRequesterCustomerDraft,
      validate: validateAssignRequesterCustomerDraft,
    },
    preconfiguration: AssignRequesterCustomerPreconfiguration,
    buildPayload: (_row, draft) => assignRequesterCustomerPayload(draft),
    commit: {
      label: "Ansprechpartner speichern",
      successMessage: "Ansprechpartner wurden gespeichert.",
      partialMessage: (result) =>
        result.failed_count
          ? `${result.success_count} Ansprechpartner gespeichert, ${result.failed_count} fehlgeschlagen.`
          : `${result.success_count} Ansprechpartner gespeichert.`,
    },
  }) satisfies InboxWorkflowTableStepConfig<
    AssignRequesterCustomerRow,
    AssignRequesterCustomerDraft,
    AssignRequesterCustomerCommitPayload,
    AssignRequesterCustomerPreviewPayload
  >
